<script>
  import { onMount, tick } from "svelte";
  import { apiRequest } from "../utils/api";
  import { ruleLabel, ruleDescription } from "../utils/rules";

  export let generationId = null;
  export let isAdmin = false;
  // Slice 5: rule ids the current run is BLOCKED on (from the timeline, owned by
  // GenerationManager). A card whose id is in this set gets a danger ring + a
  // "failing" marker so the human sees exactly which rule the agent is stuck on.
  export let flaggedRuleIds = [];
  // Part C: ask AppShell to navigate to the wide /generations/:id/rules page.
  // The rail itself is READ-ONLY — no create/edit form here anymore.
  export let onEditRules = () => {};

  // Reactive Set of the blocked rule ids. Markup must reference `flaggedSet`
  // DIRECTLY (not via a helper that closes over it) so Svelte re-renders the
  // cards when the set changes — a hidden dependency inside a function body is
  // not tracked by the compiler.
  $: flaggedSet = new Set(flaggedRuleIds || []);

  let rules = [];
  let loading = true;
  let error = null;

  // When the agent trips a guardrail its rule id lands in flaggedRuleIds. This
  // panel only mounts when the Rules accordion is EXPANDED, so if we're here the
  // rules are visible — FLASH the exact rule the model stumbled on. We react to
  // genuine ADDITIONS only (a rule newly entering the blocked set), and hold off
  // until the rules have loaded so the target <li> exists (mount with a pre-set
  // flag still flashes, once loadRules fills the list). Tracked via a sorted
  // string signature so re-assigning it to an equal value can't loop the block.
  let containerEl = null;
  let flashRuleIds = new Set();
  let prevFlaggedSig = "";

  $: reactToFlaggedRules(flaggedRuleIds, loading);

  function reactToFlaggedRules(ids, isLoading) {
    if (isLoading) {
      return; // rules not in the DOM yet — caught on the next run once loaded
    }
    const arr = (ids || []).slice().sort((a, b) => a - b);
    const signature = arr.join(",");
    if (signature === prevFlaggedSig) {
      return;
    }
    const prevSet = new Set(prevFlaggedSig ? prevFlaggedSig.split(",").map(Number) : []);
    const added = arr.filter((id) => !prevSet.has(id));
    prevFlaggedSig = signature;
    if (added.length > 0) {
      flashRules(added);
    }
  }

  function flashRules(ids) {
    const next = new Set(flashRuleIds);
    for (const id of ids) {
      next.add(id);
    }
    flashRuleIds = next;
    // NO auto-scroll. Yanking the viewport to the flagged rule hijacked the
    // operator's scroll position (a UX regression) — mirrors the transactions
    // table's flashRows fix. The flash alone signals the change; the operator
    // can jump to it on demand via the "jump to rule" affordance below.
    setTimeout(() => {
      const cleared = new Set(flashRuleIds);
      for (const id of ids) {
        cleared.delete(id);
      }
      flashRuleIds = cleared;
    }, 1400);
  }

  // Explicit, user-initiated scroll: only when the operator clicks "jump to
  // rule" do we bring the flagged rule into view (no scroll-jacking).
  function jumpToRule(ruleId) {
    tick().then(() => {
      const el = containerEl?.querySelector(`[data-rule-id="${ruleId}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }

  // Part A (rules-editor redesign): per-card "show raw" toggle, admin-only,
  // default OFF. Tracks which rule ids currently have the raw JSON panel open.
  let showRawIds = new Set();

  // Always-on defaults are not rows and have no id. Keying the each block on
  // `rule.id || index` collided the moment they arrived: a default at index 3 and
  // a real rule with id 3 produce the SAME key, and Svelte throws on duplicate
  // keys — taking the whole rail down, "Edit rules" button included. rule_type is
  // what makes a default unique, and it also keeps the raw-JSON toggle per-card
  // instead of expanding all seven at once.
  function ruleKey(rule) {
    return rule.id ?? `default:${rule.rule_type}`;
  }

  // Defaults pinned above the generation's own rules, as on the rules page, and
  // COLLAPSED — seven baseline cards in a 288px rail buried the run's own rules
  // under a long scroll. The header states they exist and how many; the detail is
  // one click away.
  $: defaultRules = rules.filter((rule) => rule.source === "default");
  $: ownRules = rules.filter((rule) => rule.source !== "default");

  let showDefaults = false;

  function toggleRaw(ruleId) {
    const next = new Set(showRawIds);
    if (next.has(ruleId)) {
      next.delete(ruleId);
    } else {
      next.add(ruleId);
    }
    showRawIds = next;
  }

  onMount(async () => {
    await loadRules();
  });

  async function loadRules() {
    if (!generationId) return;
    loading = true;
    error = null;
    try {
      rules = await apiRequest(`/api/generations/${generationId}/rules`);
    } catch (e) {
      error = e?.message || "Could not load rules";
    } finally {
      loading = false;
    }
  }
</script>

<div bind:this={containerEl}>
  <!-- No card chrome here (no border/padding/shadow): this panel is ALWAYS
       mounted inside GenerationManager's accordion, which is itself a bordered
       card with its own "Rules" header + p-3 body. A second border+p-4 here
       made a redundant "frame within a frame" and ate horizontal space in the
       288px rail. The per-rule <li> borders below are the only frames needed. -->
  <!-- No "Rules" heading here: the accordion already carries the "Rules" label. -->
  {#if error}
    <p class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-2.5 py-1 text-xs mb-3" role="alert">{error}</p>
  {:else if loading}
    <p class="text-neutral-500 text-xs">Loading rules...</p>
  {:else}
    {#if defaultRules.length > 0}
      <button
        type="button"
        class="w-full flex items-center gap-1.5 text-left mb-2.5 px-2 py-1.5 rounded-md border border-dashed border-neutral-300 bg-neutral-50 hover:bg-neutral-100 transition-colors"
        data-testid="rules-defaults-toggle"
        aria-expanded={showDefaults}
        title="Enforced on every generation with no rule record behind them. Add a rule of the same type to change or switch one off."
        on:click={() => (showDefaults = !showDefaults)}
      >
        <span class="text-[0.68rem] text-neutral-500">{showDefaults ? "▾" : "▸"}</span>
        <span class="text-[0.7rem] font-bold text-neutral-700">🔒 Always on</span>
        <span class="text-[0.66rem] text-neutral-500">{defaultRules.length}</span>
      </button>
      {#if showDefaults}
        <ul class="space-y-2.5 mb-3" data-testid="rules-defaults-list">
          {#each defaultRules as rule (ruleKey(rule))}
            <li
              class="border border-dashed border-neutral-300 rounded-md p-3 bg-neutral-50"
              data-testid="rule-item"
              data-rule-source={rule.source}
            >
              <div class="flex items-center gap-2 mb-1.5 flex-wrap">
                <span class="text-xs font-bold text-neutral-900">{rule.display?.title || ruleLabel(rule)}</span>
                <span
                  class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[0.62rem] font-bold uppercase bg-neutral-200 text-neutral-700 border border-neutral-300"
                  data-testid="rule-default-badge"
                  title="Always on for every generation. There is no rule record behind this — it is the built-in default."
                >🔒 always on</span>
              </div>
              {#if rule.display?.summary}
                <p class="text-xs text-neutral-600 mb-1.5">{rule.display.summary}</p>
              {/if}
              {#if rule.display?.params?.length > 0}
                <table class="w-full text-[0.7rem] text-neutral-700">
                  <tbody>
                    {#each rule.display.params as field}
                      <tr>
                        <td class="pr-2 py-0.5 align-top font-semibold text-neutral-500 whitespace-nowrap">{field.label}</td>
                        <td class="py-0.5 align-top break-words">{field.value}</td>
                      </tr>
                    {/each}
                  </tbody>
                </table>
              {/if}
            </li>
          {/each}
        </ul>
      {/if}
    {/if}

    {#if ownRules.length === 0}
      <p class="text-neutral-500 text-xs mb-3">No rules of your own for this generation.</p>
    {/if}

    <ul class="space-y-2.5 mb-3">
      {#each ownRules as rule, index (ruleKey(rule))}
        {@const flagged = rule.id != null && flaggedSet.has(rule.id)}
        <li
          class="border rounded-md p-3 transition-colors {flagged
            ? 'border-danger-border/60 ring-2 ring-danger-border/40 bg-danger-bg/40'
            : `border-neutral-200 hover:border-neutral-300 ${rule.source === 'project' ? 'opacity-70 bg-neutral-50/50' : 'bg-white'}`} {rule.id != null && flashRuleIds.has(rule.id) ? 'rule-flash' : ''}"
          data-testid="rule-item"
          data-flagged={flagged ? "true" : "false"}
          data-rule-source={rule.source}
          data-rule-id={rule.id}
        >
          <div class="flex items-center gap-2 mb-1.5 flex-wrap">
            <span class="text-xs font-bold text-neutral-900">{rule.display?.title || ruleLabel(rule)}</span>
            {#if flagged}
              <span
                class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[0.62rem] font-bold uppercase bg-danger-bg text-danger-text border border-danger-border/30"
                data-testid="rule-flagged-marker"
                title="The agent is currently blocked by this rule"
              >
                <span class="w-1.5 h-1.5 rounded-full bg-danger-text animate-pulse"></span>
                failing
              </span>
              <button
                type="button"
                class="text-[0.62rem] text-danger-text/80 hover:text-danger-text underline"
                data-testid="rule-jump-to"
                title="Scroll this rule into view"
                on:click={() => jumpToRule(rule.id)}
              >
                jump to rule
              </button>
            {/if}
            {#if rule.source === "project"}
              <span class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[0.62rem] font-bold uppercase bg-info-bg text-info-text border border-info-border/20">project</span>
            {:else}
              <span class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[0.62rem] font-bold uppercase bg-success-bg text-success-text border border-success-border/20">generation</span>
            {/if}
            {#if rule.scope}
              <span class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[0.62rem] font-bold uppercase bg-neutral-100 text-neutral-600 ml-auto">{rule.scope}</span>
            {/if}
          </div>
          {#if rule.display}
            <!-- Part A: rule "in words" — bold title (above) + one-line summary
                 + a small label/value table with ids already resolved to names.
                 Short human text wraps fine in the 288px rail; no more JSON. -->
            {#if rule.display.summary}
              <p class="text-xs text-neutral-600 mb-1.5">{rule.display.summary}</p>
            {/if}
            {#if rule.display.params && rule.display.params.length > 0}
              <table class="w-full text-[0.7rem] text-neutral-700 mb-1.5">
                <tbody>
                  {#each rule.display.params as field}
                    <tr>
                      <td class="pr-2 py-0.5 align-top font-semibold text-neutral-500 whitespace-nowrap">{field.label}</td>
                      <td class="py-0.5 align-top break-words">{field.value}</td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            {/if}
          {:else}
            <p class="text-xs text-neutral-500 mb-1.5">{ruleDescription(rule)}</p>
            {#if !rule.params || Object.keys(rule.params).length === 0}
              <span class="text-[0.68rem] text-neutral-400 italic block mb-1.5">No parameters required</span>
            {/if}
          {/if}

          {#if isAdmin}
            <button
              type="button"
              class="text-[0.66rem] text-neutral-400 hover:text-neutral-600 underline mb-1.5"
              data-testid="rule-show-raw-toggle"
              on:click={() => toggleRaw(ruleKey(rule))}
            >
              {showRawIds.has(ruleKey(rule)) ? "Hide raw" : "Show raw"}
            </button>
            {#if showRawIds.has(ruleKey(rule))}
              <pre class="text-[0.7rem] bg-neutral-50 border border-neutral-200/60 rounded p-2 overflow-x-auto font-mono whitespace-pre-wrap break-all text-neutral-800 mb-1.5" data-testid="rule-raw-json">{JSON.stringify(rule.params, null, 2)}</pre>
            {/if}
          {/if}
        </li>
      {/each}
    </ul>

    {#if isAdmin}
      <!-- Part C: create/edit lives on the wide /generations/:id/rules page now
           — the rail is read-only. This button is the only way in from here. -->
      <button type="button" class="btn-secondary w-full" data-testid="edit-rules-link" on:click={onEditRules}>
        Edit rules
      </button>
    {/if}
  {/if}
</div>
